import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { combineAll, WINDOW } from '@datachain/ui-sdk/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import {
	getConnectedUserFail,
	getDetailsForConnectedUser,
	getDetailsForConnectedUserSuccess,
	getOrganization,
	getOrganizationFail,
	getOrganizationSuccess,
	goToConnectedUserProfile,
	setAppLanguage,
	switchViewMode,
} from './app.actions';
import { AppSelector } from './app.selector';
import { USER_PROFILE_PATH } from '../../constants';
import { ProfileRequester } from '../../requesters/profile/profile.requester';
import { AppAdapter } from '../adapters/app.adapter';
import { ProfileAdapter } from '../adapters/profile.adapter';
import { UserOrganization } from '../domain/user-organization';
import { PreferenceStorage } from '../storage/preference.storage';

@Injectable()
export class AppEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################
	public navigateToProfile$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(goToConnectedUserProfile),
				tap(() => this.router.navigate([USER_PROFILE_PATH])),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				}),
			),
		{
			dispatch: false,
		},
	);

	// ##########################################################
	// Business Effects
	// ##########################################################
	public syncLangPref$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(setAppLanguage),
				tap((action) => {
					localStorage.setItem('pref:lang', action.lang);
					this.window.location.reload();
				}),
			),
		{
			dispatch: false,
		},
	);

	public getOrganization$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getOrganization),
			map((action) =>
				getOrganizationSuccess({
					organization: action.organization,
				}),
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					getOrganizationFail({
						error,
					}),
				);
				return caught;
			}),
		),
	);

	public getConnectedUser$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getDetailsForConnectedUser),
			switchMap(({ user, preferredLang }) =>
				combineAll({
					user: of(user),
					availableInstances:
						this.profileRequester.getAvailableInstanceModules(),
					preferences: of({
						language: preferredLang,
					}),
				}),
			),
			map(({ user, availableInstances, preferences }) => {
				const selectedLanguage =
					this.appAdapter.languageMapping[preferences.language];
				const savedLanguage = localStorage.getItem('pref:lang');

				if (selectedLanguage !== savedLanguage) {
					return setAppLanguage({
						lang: selectedLanguage,
					});
				}
				return getDetailsForConnectedUserSuccess({
					userMarketplacePreferences: this.preferenceStorage.getValue(),
					defaultOrganizationId: user.primaryOrganizationId,
					organizationPerUserAccount: UserOrganization.build({
						id: user.primaryOrganizationId,
					}),
					instanceModules: this.profileAdapter.generateAvailableInstanceModules(
						availableInstances,
						this.window.location,
					),
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				this.store.dispatch(
					getConnectedUserFail({
						error,
					}),
				);
				return caught;
			}),
		),
	);

	public switchViewMode$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(switchViewMode),
				withLatestFrom(this.appSelector.getPreferences$()),
				tap(([, userPref]) => {
					this.preferenceStorage.setViewMode(userPref);
				}),
			),
		{
			dispatch: false,
		},
	);

	public constructor(
		private readonly actions$: Actions,
		private readonly store: Store,
		private readonly appSelector: AppSelector,
		private readonly preferenceStorage: PreferenceStorage,
		private readonly router: Router,
		private readonly appAdapter: AppAdapter,
		private readonly profileAdapter: ProfileAdapter,
		private readonly profileRequester: ProfileRequester,
		@Inject(WINDOW) private readonly window: Window,
	) {}
}
